/**
 * 機器の管理画面（ブラウザ風 UI）— 汎用レンダラ。
 *
 * 【厳守している設計】
 * 1. 章ごとに画面を作り分けない。AdminScreen（JSON）を解釈するのはこの 1 つだけ。
 * 2. 表示する値は必ず world から読む。UI 側に値を持たない（ハードコード禁止）。
 * 3. 編集・操作の結果は必ず Effect に変換して applyEffect へ渡す。
 *    **この UI が world を直接書き換えることは一度も無い。**
 * 4. 適用後は world から読み直して描き直す。だから
 *    「チャンネルを変えたら電波品質の表示も変わる」が自動的に成立する。
 */

import type { AdminAction, AdminInput, AdminRow, AdminScreen, AdminTab, AdminTable } from '../../types/admin';
import type { Device, DeviceId, NetworkWorld } from '../../types/network';
import type { Condition, Effect } from '../../types/scenario';
import type { GlossaryEntry } from '../../scenario/dataSource';
import { button, clear, el } from '../dom';
import { renderTextWithTerms, type TermSource } from '../terms';
import { formatValue, readArray, readField, readPath } from './paths';
import { resolveEffects, type PlaceholderValue } from './placeholders';

export interface AdminHooks {
  /** 効果の適用を依頼する。UI は world を触らない */
  onEffects: (effects: Effect[]) => void;
  onClose: () => void;
  onTermClick: (entry: GlossaryEntry | null, term: string) => void;
  /** AdminAction.condition / AdminScreen.openCondition の判定 */
  evaluate: (condition: Condition | undefined) => boolean;
  /**
   * 確認の差し替え。**通常は渡さない。**
   *
   * 省略すると、この画面が自前の確認ウィンドウ（ゲーム内 UI）を出す。
   * ブラウザ既定の confirm は使わない。埋め込みブラウザやサンドボックスでは
   * 抑止されて操作が無言で失敗し、「守られている」と誤読させるため。
   * ここを渡してよいのは、確認を挟まず結果だけを検査したいテストのような
   * 呼び出し側に限る。
   */
  confirm?: (message: string) => boolean;
}

/** deviceId 指定を優先し、無ければ kind で一致させる */
export function findAdminScreen(
  screens: AdminScreen[],
  world: NetworkWorld,
  deviceId: DeviceId,
): AdminScreen | null {
  const device = world.devices[deviceId];
  if (!device) return null;
  const byDevice = screens.find((screen) => screen.deviceId === deviceId);
  if (byDevice) return byDevice;
  return screens.find((screen) => !screen.deviceId && screen.deviceKind === device.kind) ?? null;
}

export class AdminScreenView {
  readonly root: HTMLElement;

  private readonly headTitle: HTMLElement;
  private readonly urlBar: HTMLElement;
  private readonly tabBar: HTMLElement;
  private readonly bodyNode: HTMLElement;
  private readonly messageNode: HTMLElement;
  /** ゲーム内の確認ウィンドウ（window.confirm の代わり） */
  private readonly confirmNode: HTMLElement;

  private screen: AdminScreen | null = null;
  private world: NetworkWorld | null = null;
  private deviceId: DeviceId = '';
  private activeTabId = '';
  /** 確認待ちの操作。「はい」を押したときに実行する */
  private pendingConfirm: (() => void) | null = null;

  constructor(
    private readonly termSource: TermSource,
    private readonly hooks: AdminHooks,
  ) {
    this.headTitle = el('span', { class: 'admin-title' });
    this.urlBar = el('div', { class: 'admin-url' });
    this.tabBar = el('div', { class: 'admin-tabs' });
    this.bodyNode = el('div', { class: 'admin-body' });
    this.messageNode = el('div', { class: 'admin-message', hidden: true });
    this.confirmNode = el('div', { class: 'admin-confirm', hidden: true });

    this.root = el('div', { class: 'admin-layer', hidden: true }, [
      el('div', { class: 'admin-window' }, [
        el('div', { class: 'admin-head' }, [
          this.headTitle,
          button('閉じる (Esc)', () => this.hooks.onClose(), 'btn btn-ghost btn-small'),
        ]),
        this.urlBar,
        this.tabBar,
        this.bodyNode,
        this.messageNode,
        this.confirmNode,
      ]),
    ]);
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  /** 確認ウィンドウを出しているか */
  get isConfirmOpen(): boolean {
    return this.pendingConfirm !== null;
  }

  get currentDeviceId(): DeviceId {
    return this.deviceId;
  }

  open(screen: AdminScreen, world: NetworkWorld, deviceId: DeviceId): void {
    this.screen = screen;
    this.world = world;
    this.deviceId = deviceId;
    this.activeTabId = screen.tabs[0]?.id ?? '';
    this.setMessage(null);
    this.closeConfirm();
    this.root.hidden = false;
    this.render();
  }

  close(): void {
    this.root.hidden = true;
    this.screen = null;
    this.world = null;
    this.setMessage(null);
    this.closeConfirm();
  }

  /** world が変わったら呼ぶ。値は必ず読み直す */
  render(): void {
    const screen = this.screen;
    const world = this.world;
    if (!screen || !world) return;
    const device = world.devices[this.deviceId] ?? null;

    this.headTitle.textContent = screen.title;
    clear(this.urlBar);
    this.urlBar.append(
      el('span', { class: 'admin-url-scheme', text: 'http://' }),
      el('span', {
        class: 'admin-url-host',
        text: device?.ip.address ?? '（アドレス未設定）',
      }),
      el('span', { class: 'admin-url-path', text: `/admin/${this.activeTabId}` }),
    );

    this.renderTabs(screen.tabs);
    clear(this.bodyNode);

    if (!device) {
      this.bodyNode.append(
        el('p', { class: 'admin-empty', text: 'この機器は見つかりません。' }),
      );
      return;
    }

    const tab = screen.tabs.find((t) => t.id === this.activeTabId) ?? screen.tabs[0];
    if (!tab) {
      this.bodyNode.append(el('p', { class: 'admin-empty', text: '表示する項目がありません。' }));
      return;
    }
    this.renderTab(tab, device);
  }

  // ------------------------------------------------------------
  // 描画
  // ------------------------------------------------------------

  private renderTabs(tabs: AdminTab[]): void {
    clear(this.tabBar);
    for (const tab of tabs) {
      this.tabBar.append(
        button(
          tab.label,
          () => {
            this.activeTabId = tab.id;
            this.setMessage(null);
            this.render();
          },
          this.activeTabId === tab.id ? 'admin-tab active' : 'admin-tab',
        ),
      );
    }
  }

  private renderTab(tab: AdminTab, device: Device): void {
    if (tab.description) {
      this.bodyNode.append(el('p', { class: 'admin-desc', text: tab.description }));
    }
    for (const row of tab.rows ?? []) {
      this.bodyNode.append(this.renderRow(row, device));
    }
    if (tab.table) {
      this.bodyNode.append(this.renderTable(tab.table, device));
    }
    const actions = tab.actions ?? [];
    if (actions.length > 0) {
      const bar = el('div', { class: 'admin-actions' });
      for (const action of actions) bar.append(this.renderAction(action, {}));
      this.bodyNode.append(bar);
    }
  }

  private renderRow(row: AdminRow, device: Device): HTMLElement {
    const value = readPath(device, row.path);

    const label = el('div', { class: 'admin-row-label' });
    label.append(
      renderTextWithTerms(row.label, row.terms, this.termSource, this.hooks.onTermClick),
    );

    const valueNode = el('div', { class: 'admin-row-value', text: formatValue(value) });

    const node = el('div', { class: 'admin-row' }, [
      label,
      valueNode,
      row.editable ? this.renderEditor(row, value) : el('div', { class: 'admin-row-edit' }),
    ]);

    if (row.note) {
      const note = el('div', { class: 'admin-row-note' });
      note.append(
        renderTextWithTerms(row.note, row.terms, this.termSource, this.hooks.onTermClick),
      );
      node.append(note);
    }
    return node;
  }

  /** 編集欄。確定すると onChange の Effect を、$value を埋めて適用依頼する */
  private renderEditor(row: AdminRow, value: unknown): HTMLElement {
    const wrapper = el('div', { class: 'admin-row-edit' });

    if (!row.onChange || row.onChange.length === 0) {
      // 変更手段（Effect）が書かれていない行は編集できない。
      // UI が world を直接書き換えることはしないので、ここは読み取り専用に落とす。
      console.warn(`[admin] 行「${row.label}」は editable ですが onChange がありません`);
      wrapper.append(el('span', { class: 'admin-readonly', text: '（変更できません）' }));
      return wrapper;
    }

    const commit = (next: PlaceholderValue): void => {
      this.applyEffects(this.resolve(row.onChange, { $value: next }), `${row.label}を変更しました`);
    };

    const input: AdminInput = row.input ?? { kind: 'text' };
    switch (input.kind) {
      case 'select': {
        const select = el('select', { class: 'admin-input admin-select' });
        for (const option of input.options) {
          const optionNode = el('option', {
            value: String(option.value),
            text: option.label,
          });
          if (String(option.value) === String(value)) optionNode.selected = true;
          select.append(optionNode);
        }
        select.addEventListener('change', () => {
          const chosen = input.options.find((o) => String(o.value) === select.value);
          if (chosen) commit(chosen.value);
        });
        wrapper.append(select);
        break;
      }
      case 'number': {
        const field = el('input', {
          class: 'admin-input',
          type: 'number',
          value: typeof value === 'number' ? String(value) : '',
          min: input.min,
          max: input.max,
          step: input.step ?? 1,
        });
        const apply = (): void => {
          const next = Number(field.value);
          if (!Number.isFinite(next)) return;
          commit(next);
        };
        field.addEventListener('change', apply);
        wrapper.append(field, button('適用', apply, 'btn btn-small'));
        break;
      }
      case 'toggle': {
        const field = el('input', { class: 'admin-toggle', type: 'checkbox' });
        field.checked = value === true;
        field.addEventListener('change', () => commit(field.checked));
        wrapper.append(field);
        break;
      }
      case 'text':
      default: {
        const field = el('input', {
          class: 'admin-input',
          type: 'text',
          value: value === null || value === undefined ? '' : String(value),
        });
        const apply = (): void => commit(field.value);
        field.addEventListener('change', apply);
        wrapper.append(field, button('適用', apply, 'btn btn-small'));
        break;
      }
    }
    return wrapper;
  }

  /**
   * 表（ファイアウォールのルール一覧など）。
   * reorderable なら「上へ／下へ」を出す。順序は order 欄があればそれで、
   * 無ければ配列の並びで判断する。
   *
   * 【固定行（editable: false）】
   * その行の「↑」「↓」は押せない。さらに、可動行は固定行を飛び越えて動くので、
   * **他の行をどう動かしても固定行の位置は変わらない**（sim 側と同じ規則）。
   * 画面の説明文「鍵のかかった行は動かせません」と実際の挙動を一致させる。
   *
   * 表は横に長くなりがちなので、スクロールできる箱に入れて出す。
   * 狭い画面でも中身が読める（文字が縦一列に潰れない）ことを優先する。
   */
  private renderTable(table: AdminTable, device: Device): HTMLElement {
    const items = readArray(device, table.path);
    const sorted = sortByOrder(items);

    if (sorted.length === 0) {
      return el('p', { class: 'admin-empty', text: '項目がありません。' });
    }

    const head = el('tr', {});
    if (table.reorderable) head.append(el('th', { class: 'admin-th admin-th-reorder', text: '順序' }));
    for (const column of table.columns) {
      const th = el('th', { class: 'admin-th', text: column.label });
      if (column.width) th.style.width = column.width;
      head.append(th);
    }
    if ((table.rowActions ?? []).length > 0) {
      head.append(el('th', { class: 'admin-th admin-th-actions', text: '操作' }));
    }

    const body = el('tbody', {});
    sorted.forEach((item, index) => {
      // editable: false の行は「中身も順序も変えられない」。
      // 押せるのに何も起きないボタンは、守られていると誤読させるので出さない
      const locked = !isMovable(item);
      const row = el('tr', { class: locked ? 'admin-tr admin-tr-locked' : 'admin-tr' });
      if (table.reorderable) {
        row.append(this.renderReorderCell(table, sorted, index));
      }
      for (const column of table.columns) {
        row.append(el('td', { class: 'admin-td', text: formatValue(readField(item, column.field)) }));
      }
      const rowActions = table.rowActions ?? [];
      if (rowActions.length > 0) {
        const cell = el('td', { class: 'admin-td admin-td-actions' });
        for (const action of rowActions) {
          cell.append(
            this.renderAction(
              action,
              { $id: idOf(item, index), $order: orderOf(item, index) },
              locked ? '変更不可の行です。会社の決まりなので変えられません。' : null,
            ),
          );
        }
        row.append(cell);
      }
      body.append(row);
    });

    const node = el('table', { class: 'admin-table' }, [el('thead', {}, [head]), body]);
    return el('div', { class: 'admin-table-wrap' }, [node]);
  }

  /** 「↑」「↓」の欄。動かせない行は押せない状態にし、理由を添える */
  private renderReorderCell(table: AdminTable, sorted: unknown[], index: number): HTMLElement {
    const locked = !isMovable(sorted[index]);
    const up = neighbourMovableIndex(sorted, index, -1);
    const down = neighbourMovableIndex(sorted, index, +1);

    const make = (label: string, target: number | null, direction: string): HTMLButtonElement => {
      const node = button(
        label,
        () => this.moveRow(table, sorted, index, target),
        target === null ? 'btn btn-small disabled' : 'btn btn-small',
      );
      node.disabled = target === null;
      node.title = locked
        ? '変更不可の行です。会社の決まりなので順序も変えられません。'
        : target === null
          ? `これより${direction}へは動かせません。`
          : `この行を1つ${direction}へ動かす`;
      return node;
    };

    return el('td', { class: 'admin-td admin-reorder' }, [
      el('div', { class: 'admin-reorder-buttons' }, [
        make('↑', up, '上'),
        make('↓', down, '下'),
      ]),
    ]);
  }

  /** 上へ／下へ。移動先の行が持つ順序の値を $order として渡す */
  private moveRow(table: AdminTable, sorted: unknown[], index: number, targetIndex: number | null): void {
    if (targetIndex === null) return;
    if (!isMovable(sorted[index])) return;
    if (!table.onReorder || table.onReorder.length === 0) {
      console.warn('[admin] reorderable ですが onReorder がありません');
      return;
    }
    const effects = this.resolve(table.onReorder, {
      $id: idOf(sorted[index], index),
      $order: orderOf(sorted[targetIndex], targetIndex),
    });
    this.applyEffects(effects, '順序を入れ替えました');
  }

  /**
   * 操作ボタン。
   * `lockedReason` が渡された行では押せない状態にし、その理由を吹き出しに出す。
   */
  private renderAction(
    action: AdminAction,
    vars: Record<string, PlaceholderValue>,
    lockedReason: string | null = null,
  ): HTMLElement {
    const enabled = lockedReason === null && this.hooks.evaluate(action.condition);
    const node = button(
      action.label,
      () => {
        if (!enabled) return;
        const run = (): void =>
          this.applyEffects(this.resolve(action.effects, vars), action.resultText ?? null);
        if (action.confirm) {
          this.requestConfirm(action.confirm, run);
          return;
        }
        run();
      },
      enabled ? 'btn btn-small admin-action' : 'btn btn-small admin-action disabled',
    );
    node.disabled = !enabled;
    // 省略表記のボタンでも、何が起きるか（押せないなら理由）を補えるようにする
    node.title = lockedReason ?? action.confirm ?? action.label;
    return node;
  }

  // ------------------------------------------------------------
  // 確認（ゲーム内 UI）
  // ------------------------------------------------------------

  /**
   * 実行前の確認。
   *
   * **ブラウザ既定の confirm は使わない。**
   * 埋め込みブラウザやサンドボックスでは抑止され、押しても何も起きない
   * ＝「守られている」ように見えてしまうため。ここでは画面の中に
   * 会話ウィンドウと同じ見た目の確認を出し、「はい」を押したときだけ実行する。
   */
  private requestConfirm(message: string, onOk: () => void): void {
    if (this.hooks.confirm) {
      // 差し替えが渡されている場合だけそれに従う（テストなどの呼び出し側）
      if (this.hooks.confirm(message)) onOk();
      return;
    }
    this.pendingConfirm = onOk;
    clear(this.confirmNode);
    this.confirmNode.append(
      el('div', { class: 'admin-confirm-box' }, [
        el('div', { class: 'admin-confirm-head', text: '確認' }),
        el('p', { class: 'admin-confirm-text', text: message }),
        el('div', { class: 'admin-confirm-actions' }, [
          button('はい、実行する', () => this.resolveConfirm(true), 'btn btn-small admin-confirm-ok'),
          button('いいえ、やめておく', () => this.resolveConfirm(false), 'btn btn-small'),
        ]),
      ]),
    );
    this.confirmNode.hidden = false;
  }

  private resolveConfirm(ok: boolean): void {
    const pending = this.pendingConfirm;
    this.closeConfirm();
    if (ok && pending) pending();
  }

  private closeConfirm(): void {
    this.pendingConfirm = null;
    this.confirmNode.hidden = true;
    clear(this.confirmNode);
  }

  /**
   * Esc を受け取る窓口。
   * 確認を出していれば取り消して true（画面はまだ閉じない）。
   */
  dismissConfirm(): boolean {
    if (!this.pendingConfirm) return false;
    this.resolveConfirm(false);
    return true;
  }

  // ------------------------------------------------------------
  // 効果の適用
  // ------------------------------------------------------------

  /**
   * 画面定義の Effect にプレースホルダを埋める。
   *
   * **`$device` は必ずここで足す。**
   * deviceKind で選ぶ画面（無線 AP）は複数の機器で共用するため、
   * 画面定義に機器 id を書けない。代わりに `"$device"` と書いてあり、
   * 「いま開いている機器」に置き換えるのはこの UI の責務になる。
   * 呼び出し側が渡した値を優先させないよう、$device は後から上書きする。
   */
  private resolve(effects: Effect[] | undefined, vars: Record<string, PlaceholderValue>): Effect[] {
    return resolveEffects(effects, { ...vars, $device: this.deviceId });
  }

  private applyEffects(effects: Effect[], message: string | null): void {
    if (effects.length === 0) return;
    this.hooks.onEffects(effects);
    this.setMessage(message);
    // world を読み直して描き直す。表示は常に世界の実体に追従する
    this.render();
  }

  private setMessage(text: string | null): void {
    if (!text) {
      this.messageNode.hidden = true;
      this.messageNode.textContent = '';
      return;
    }
    this.messageNode.hidden = false;
    this.messageNode.textContent = text;
  }
}

// ---------------------------------------------------------------
// 表の並び
// ---------------------------------------------------------------

function orderOf(item: unknown, fallbackIndex: number): number {
  const value = readField(item, 'order');
  return typeof value === 'number' && Number.isFinite(value) ? value : fallbackIndex;
}

function idOf(item: unknown, fallbackIndex: number): string {
  const value = readField(item, 'id');
  return typeof value === 'string' ? value : String(fallbackIndex);
}

/** 動かしてよい行か。editable: false の行は順序も固定 */
function isMovable(item: unknown): boolean {
  return readField(item, 'editable') !== false;
}

/**
 * 「1 つ上（下）の動かせる行」を探す。
 * 固定行は飛び越える（固定行の位置は動かさないため）。
 * 動かせる行が無ければ null＝そのボタンは押せない。
 */
function neighbourMovableIndex(
  sorted: unknown[],
  index: number,
  delta: -1 | 1,
): number | null {
  if (!isMovable(sorted[index])) return null;
  for (let i = index + delta; i >= 0 && i < sorted.length; i += delta) {
    if (isMovable(sorted[i])) return i;
  }
  return null;
}

/** order 欄があればその昇順。無ければ元の並びのまま */
function sortByOrder(items: unknown[]): unknown[] {
  return items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => orderOf(a.item, a.index) - orderOf(b.item, b.index) || a.index - b.index)
    .map((entry) => entry.item);
}
